import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '../Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fullScreen';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  /** When true, hides the built-in header (title/close button) for custom implementations */
  hideHeader?: boolean;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]',
  fullScreen: 'w-screen h-screen max-w-none',
};

// Global reference counter for managing body scroll lock across nested modals
let modalOpenCount = 0;
let originalBodyOverflow: string | null = null;

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
  hideHeader = false,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle mount/unmount with animation
  useEffect(() => {
    if (isOpen) {
      // Start rendering and trigger enter animation
      setShouldRender(true);
      setIsClosing(false);
      // Small delay to ensure DOM is ready for animation
      requestAnimationFrame(() => {
        // Animation will trigger via CSS classes
      });
    } else if (shouldRender) {
      // Trigger exit animation
      setIsClosing(true);
      // Wait for exit animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 200); // Match animation duration (--duration-fast: 200ms)
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape || isClosing) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape, isClosing]);

  // Focus management and scroll to top
  useEffect(() => {
    if (isOpen && !isClosing) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      modalRef.current?.focus();
      // Scroll modal container to top when opening
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    } else if (!shouldRender) {
      previousActiveElement.current?.focus();
    }
  }, [isOpen, isClosing, shouldRender]);

  // Prevent body scroll when modal is open
  // Use reference counting to handle nested modals correctly
  useEffect(() => {
    if (shouldRender && !isClosing) {
      // Save original overflow style only on first modal
      if (modalOpenCount === 0) {
        originalBodyOverflow = window.getComputedStyle(document.body).overflow;
      }
      
      // Increment counter and lock body scroll
      modalOpenCount++;
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Decrement counter
        modalOpenCount--;
        
        // Only restore scroll when all modals are closed
        if (modalOpenCount === 0 && originalBodyOverflow !== null) {
          document.body.style.overflow = originalBodyOverflow;
          originalBodyOverflow = null;
        }
      };
    }
  }, [shouldRender, isClosing]);

  if (!shouldRender) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-modal overflow-y-auto"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 transition-opacity duration-200',
          isClosing ? 'opacity-0' : 'opacity-100'
        )}
        aria-hidden="true"
        onClick={closeOnOverlayClick && !isClosing ? onClose : undefined}
      />

      {/* Modal */}
      <div className={cn(
        "flex min-h-full items-start justify-center",
        size === 'fullScreen' ? 'p-0' : 'p-4 sm:p-6 md:p-8'
      )}>
        <div
          ref={modalRef}
          tabIndex={-1}
          className={cn(
            'relative w-full bg-background shadow-xl',
            'transform transition-all duration-200 ease-out',
            isClosing
              ? 'opacity-0 scale-95 animate-scale-out'
              : 'opacity-100 scale-100 animate-scale-in',
            size === 'fullScreen' ? 'rounded-none' : 'rounded-lg my-8',
            sizeClasses[size],
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - hidden when hideHeader is true or fullScreen with custom content */}
          {!hideHeader && size !== 'fullScreen' && (
            <div className="flex items-start justify-between p-6 pb-4">
              <div>
                {title && (
                  <h2 id="modal-title" className="text-lg font-semibold text-foreground">
                    {title}
                  </h2>
                )}
                {description && (
                  <p id="modal-description" className="mt-1 text-sm text-muted-foreground">
                    {description}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="ml-4 rounded-md p-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Close modal"
              >
                <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Body - no padding for fullScreen, standard padding otherwise */}
          <div className={size === 'fullScreen' ? '' : 'px-6 py-4'}>{children}</div>

          {/* Footer - hidden for fullScreen (caller manages own footer) */}
          {footer && size !== 'fullScreen' && (
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Dialog component - a specific type of modal with standard actions
export interface DialogProps extends Omit<ModalProps, 'footer'> {
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'destructive' | 'danger';
  isLoading?: boolean;
}

export function Dialog({
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  isLoading = false,
  ...modalProps
}: DialogProps) {
  return (
    <Modal
      {...modalProps}
      footer={
        <>
          <Button variant="ghost" onClick={modalProps.onClose} disabled={isLoading}>
            {cancelText}
          </Button>
          {onConfirm && (
            <Button variant={confirmVariant} onClick={onConfirm} isLoading={isLoading}>
              {confirmText}
            </Button>
          )}
        </>
      }
    />
  );
}
